import type { ExperimentConfig } from '@/types';

/**
 * Experiment 11: Determination of the Empirical Formula of a Compound
 * CHEM-1131 Laboratory
 *
 * Students determine the empirical formula of tin oxide by reacting
 * tin with nitric acid and calculating the mole ratio of Sn to O.
 */
const experiment11Config: ExperimentConfig = {
  id: 'exp11',
  number: 11,
  name: 'Empirical Formula Determination',
  description: 'Determine the empirical formula of tin oxide by reacting tin metal with nitric acid, heating to constant mass, and calculating the mole ratio of tin to oxygen.',
  estimatedTime: '120 minutes',
  learningObjectives: [
    'Understand the difference between empirical and molecular formulas',
    'Apply the law of conservation of mass',
    'Perform heating to constant mass technique',
    'Calculate mole ratios from experimental mass data',
    'Derive empirical formula from mole ratios'
  ],
  safetyNotes: [
    'FUME HOOD REQUIRED - Concentrated HNO3 releases toxic NO2 fumes',
    'Concentrated nitric acid causes severe burns',
    'Hot crucible - use tongs only',
    'Allow proper cooling in desiccator before weighing',
    'Brown NO2 gas is toxic - do not inhale',
    'NEVER touch crucible with bare hands — finger oils affect mass measurements',
    'Keep lid slightly ajar during heating to allow gas escape',
    'Disposal: Nitric acid must be disposed of properly (see Appendix G)',
    'Never return unused chemicals to reagent bottles — dispose as directed by instructor'
  ],
  requiredEquipment: [
    'Retort stand with iron ring',
    'Clay triangle',
    'Crucible with lid (porcelain, 15-30 mL)',
    'Bunsen burner',
    'Crucible tongs',
    'Wire gauze (optional, for support)',
    'Desiccator (with silica gel or CaCl₂)',
    'Analytical balance (to 0.001 g)',
    'Watch glass',
    'Concentrated HNO₃ (68-70%)',
    'Tin foil (~1.0 g)',
    'Fume hood'
  ],
  parts: [
    {
      id: 1,
      name: 'setup',
      title: 'Part I: Setup and Safety',
      equipment: ['Retort stand', 'Iron ring', 'Clay triangle', 'Crucible with lid'],
      initialState: {
        safetyAcknowledged: false,
        fumeHoodReady: false,
        assemblyComplete: false
      },
      steps: [
        {
          id: 1,
          title: 'Safety Briefing',
          shortDescription: 'Review fume hood and acid safety procedures',
          handler: 'handleSafetyAcknowledgement',
          infoContent: {
            title: 'Understanding Empirical Formulas',
            content: 'The empirical formula gives the simplest whole number ratio of atoms in a compound. In this experiment, we will form tin oxide and determine whether it is SnO or SnO₂.',
            whatHappens: 'You review the theoretical basis for the experiment: reacting tin metal with nitric acid converts it to tin oxide, and by measuring masses before and after, you can determine the Sn:O mole ratio.',
            observe: [
              'The difference between empirical formula (simplest ratio) and molecular formula',
              'Two possible products: SnO (tin II oxide) or SnO₂ (tin IV oxide)',
              'The law of conservation of mass underpins the entire calculation',
            ],
          }
        },
        {
          id: 2,
          title: 'Check Fume Hood',
          shortDescription: 'Verify fume hood is operating correctly',
          handler: 'handleCheckFumeHood',
          infoContent: {
            title: 'Fume Hood Requirement',
            content: 'This experiment MUST be done in a fume hood. The reaction with nitric acid produces toxic brown NO₂ gas. Keep the sash lowered at all times during acid addition.',
            whatHappens: 'You verify that the fume hood is operating correctly by checking airflow. The fume hood will contain the toxic brown NO₂ fumes produced during the reaction of tin with concentrated nitric acid.',
            observe: [
              'A tissue or strip of paper held at the sash opening should be drawn inward, confirming airflow',
              'The sash should move freely and lock at the correct working height',
              'The fume hood light and exhaust fan should both be operational',
            ],
          }
        },
        {
          id: 3,
          title: 'Assemble Ring Stand',
          shortDescription: 'Clamp an iron ring on a retort stand and place a clay triangle on it',
          handler: 'handleAssembleRingStand'
        },
        {
          id: 4,
          title: 'Position Crucible',
          shortDescription: 'Place a clean crucible and lid on the clay triangle, then put an unlighted Bunsen burner beneath',
          handler: 'handlePositionCrucible'
        }
      ]
    },
    {
      id: 2,
      name: 'initial_heating',
      title: 'Part II: Heat Crucible to Constant Mass',
      equipment: ['Bunsen burner', 'Crucible tongs', 'Desiccator', 'Analytical balance'],
      initialState: {
        burnerLit: false,
        flameIntensity: 'off',
        crucibleTemp: 25,
        crucibleMass: null,
        constantMassAchieved: false,
        massAttempts: []
      },
      steps: [
        {
          id: 1,
          title: 'Light Bunsen Burner',
          shortDescription: 'Light the burner and adjust to a strong blue flame',
          handler: 'handleLightBurner'
        },
        {
          id: 2,
          title: 'Heat Crucible',
          shortDescription: 'Raise temperature gradually and heat crucible and lid to redness',
          handler: 'handleHeatCrucible',
          infoContent: {
            title: 'Heating Purpose',
            content: 'Raise the temperature of the crucible and lid gradually, and heat them to redness. This removes moisture and volatile contaminants to ensure accurate mass measurements. Cool in a desiccator for **ten minutes**, then bring to a constant weight within **0.01 g**. Record all masses to **0.001 g**.',
            whatHappens: 'Heating the crucible and lid to redness drives off adsorbed moisture, organic residues, and any volatile contaminants. This ensures the crucible mass is stable and reproducible before adding the tin sample.',
            observe: [
              'The crucible gradually changes color from gray to orange and then glows red-orange at high temperature',
              'Any moisture or organic residue burns off, sometimes producing a brief wisp of smoke',
              'The crucible should be handled only with tongs after heating — never with bare hands',
            ],
          }
        },
        {
          id: 3,
          title: 'Transfer to Desiccator',
          shortDescription: 'Use tongs to transfer hot crucible to desiccator',
          handler: 'handleTransferToDesiccator',
          infoContent: {
            title: 'Desiccator Use',
            content: 'Always cool hot objects in a desiccator to prevent moisture absorption from the air. Silica gel in the desiccator absorbs water.',
            whatHappens: 'The hot crucible is transferred to the desiccator using tongs. Inside the sealed desiccator, silica gel absorbs any water vapor, keeping the atmosphere dry so the crucible does not pick up moisture as it cools.',
            observe: [
              'The desiccator lid should be slid (not lifted) into place to maintain the seal',
              'Silica gel indicator beads should be blue (dry); pink beads indicate the desiccant is spent',
              'The crucible needs approximately 10 minutes to cool to room temperature',
            ],
          }
        },
        {
          id: 4,
          title: 'Cool and Weigh',
          shortDescription: 'After cooling (~10 min), weigh crucible and lid',
          handler: 'handleCoolAndWeigh'
        },
        {
          id: 5,
          title: 'Check Constant Mass',
          shortDescription: 'Repeat heating, cooling in desiccator, and weighing until constant weight within 0.01 g',
          handler: 'handleCheckConstantMass',
          validation: {
            requires: ['constantMassAchieved']
          },
          infoContent: {
            title: 'Constant Mass Criterion',
            content: 'Repeat heating, cooling in the desiccator (ten minutes), and weighing until two consecutive masses agree within **0.01 g**. Record this as the constant mass of crucible + lid.',
            whatHappens: 'You cycle through heating, cooling in the desiccator, and weighing until consecutive mass readings agree within 0.01 g. This confirms all volatile material has been removed and the crucible mass is stable.',
            observe: [
              'Record each mass to 0.001 g — compare consecutive readings to check for agreement within 0.01 g',
              'If mass is still decreasing, more volatile contaminants remain and another heating cycle is needed',
              'Typically 2-3 heating cycles are sufficient to achieve constant mass',
            ],
          }
        }
      ]
    },
    {
      id: 3,
      name: 'tin_preparation',
      title: 'Part III: Add Tin Foil',
      equipment: ['Analytical balance', 'Tin foil strips', 'Crucible'],
      initialState: {
        tinMass: null,
        tinPrepared: false
      },
      steps: [
        {
          id: 1,
          title: 'Weigh Tin Foil',
          shortDescription: 'Weigh approximately 1.0 g of tin foil to 0.001 g',
          handler: 'handleWeighTin',
          validation: {
            condition: 'tinMass >= 0.9 && tinMass <= 1.1'
          },
          infoContent: {
            title: 'Tin Preparation',
            content: '**Do NOT fold the tin — BREAK it** into small pieces. Folding creates thick pieces with trapped air; breaking creates thin pieces with maximum surface area for reaction. Record the exact mass to **0.001 g**. Spread pieces evenly in the bottom of the crucible.',
            whatHappens: 'You weigh approximately 1.0 g of tin foil on the analytical balance, then break (not fold) it into small pieces to maximize surface area. The pieces are spread evenly in the bottom of the crucible for uniform reaction with nitric acid.',
            observe: [
              'The tin foil is shiny silver metallic — note its appearance before reaction',
              'Breaking produces thin, flat pieces; folding would create thick lumps with trapped air pockets',
              'The exact mass must be recorded to 0.001 g as it is critical for the empirical formula calculation',
            ],
          }
        },
        {
          id: 2,
          title: 'Break into Pieces',
          shortDescription: 'Break tin foil into small pieces',
          handler: 'handleBreakTin'
        },
        {
          id: 3,
          title: 'Add to Crucible',
          shortDescription: 'Transfer tin pieces to the crucible',
          handler: 'handleAddTinToCrucible'
        }
      ]
    },
    {
      id: 4,
      name: 'acid_addition',
      title: 'Part IV: Add Nitric Acid (IN FUME HOOD)',
      equipment: ['Concentrated HNO3', 'Dropper', 'Fume hood'],
      initialState: {
        fumeHoodSashLowered: false,
        acidDrops: 0,
        tinDissolvedPercent: 0,
        fumesIntensity: 0,
        reactionComplete: false
      },
      steps: [
        {
          id: 1,
          title: 'Lower Fume Hood Sash',
          shortDescription: 'Lower the fume hood sash to safe working level',
          handler: 'handleLowerSash',
          validation: {
            requires: ['fumeHoodSashLowered']
          }
        },
        {
          id: 2,
          title: 'Add Nitric Acid Dropwise',
          shortDescription: 'Add concentrated HNO₃ cautiously, dropwise (~1-2 mL total)',
          handler: 'handleAddAcid',
          infoContent: {
            title: 'The Tin-Nitric Acid Reaction',
            content: 'Tin reacts with concentrated nitric acid producing brown NO₂ fumes. Add acid **cautiously, dropwise**. Carefully observe the reaction after the addition of each portion. Do not add the nitric acid so fast that it foams or splatters out of the crucible. Total acid needed: approximately **1-2 mL**. **Warning:** violent bubbling, splattering, or dense brown fumes mean you are adding too fast — STOP and wait.',
            chemicalEquation: 'Sn + 4HNO₃ → SnO₂ + 4NO₂ + 2H₂O',
            whatHappens: 'Concentrated nitric acid oxidizes the tin metal. Brown nitrogen dioxide (NO₂) gas is evolved as the tin dissolves and is converted to hydrated tin oxide. The reaction is exothermic and must be controlled by adding acid dropwise.',
            observe: [
              'Brown NO₂ fumes rising from the crucible — these are toxic and must stay inside the fume hood',
              'Gentle bubbling and fizzing as each drop of acid contacts the tin metal',
              'The shiny silver tin pieces gradually dissolve, leaving a white or off-white residue',
              'If adding too fast: violent foaming, splattering, or dense brown clouds — stop immediately and wait',
            ],
          }
        },
        {
          id: 3,
          title: 'Continue Until Complete',
          shortDescription: 'After tin has completely reacted (brown fumes stop), place crucible on ring stand (IN THE FUME HOOD)',
          handler: 'handleContinueReaction',
          validation: {
            requires: ['reactionComplete']
          }
        }
      ]
    },
    {
      id: 5,
      name: 'evaporation',
      title: 'Part V: Evaporate Nitric Acid',
      equipment: ['Bunsen burner (low flame)', 'Fume hood'],
      initialState: {
        evaporationProgress: 0,
        fumesActive: true,
        evaporationComplete: false
      },
      steps: [
        {
          id: 1,
          title: 'Heat Gently',
          shortDescription: 'Use LOW flame to evaporate excess nitric acid (10-15 min)',
          handler: 'handleGentleHeat',
          infoContent: {
            title: 'Gentle Evaporation',
            content: '**CRITICAL: Do not heat the crucible strongly before all of the nitric acid has been evaporated, or the sample may splatter (causing bad burns to the skin) and throw your experimental results off!** Warm very gently, using a low flame on the burner. Ensure the sash on the fume hood is set to the lowest working level possible. Continue until no more liquid is visible and no more brown fumes evolve.',
            whatHappens: 'Gentle heating evaporates the excess nitric acid and water from the crucible. The remaining brown NO₂ fumes escape as the liquid is driven off, leaving a dry solid residue of hydrated tin oxide.',
            observe: [
              'Brown NO₂ fumes continue to evolve as the excess acid evaporates — keep in fume hood',
              'The liquid in the crucible gradually decreases in volume over 10-15 minutes',
              'When evaporation is complete, no more liquid is visible and no more brown fumes are produced',
              'A dry, powdery residue remains in the bottom of the crucible',
            ],
          }
        },
        {
          id: 2,
          title: 'Monitor Evaporation',
          shortDescription: 'Continue until brown fumes stop evolving',
          handler: 'handleMonitorEvaporation',
          validation: {
            requires: ['evaporationComplete']
          }
        }
      ]
    },
    {
      id: 6,
      name: 'strong_heating',
      title: 'Part VI: Strong Heating to Form Oxide',
      equipment: ['Bunsen burner (high flame)', 'Timer'],
      initialState: {
        flameIntensity: 'high',
        heatingTime: 0,
        crucibleGlowing: false,
        residueColor: '#696969'
      },
      steps: [
        {
          id: 1,
          title: 'Increase Flame',
          shortDescription: 'Increase to strong, hot flame',
          handler: 'handleIncreaseFlame'
        },
        {
          id: 2,
          title: 'Heat to Red Hot',
          shortDescription: 'Heat until crucible glows red-orange for at least 10 minutes',
          handler: 'handleHeatToRedHot',
          infoContent: {
            title: 'Converting to Oxide',
            content: 'Once the residue is dry (no more fumes), increase to a strong, hot flame. Heat the crucible until it glows red-orange and maintain strong heating for about **ten minutes**. Keep the lid **slightly ajar** for gas escape. Check for evidence of solid tin metal; if any remains, repeat the acid addition procedure from step 4. The product should be white (SnO₂). If the residue is **blue-black or red**, this indicates **incomplete oxidation** (SnO formation) — continue heating.',
            whatHappens: 'Strong heating at red-hot temperatures completes the conversion of hydrated tin oxide to anhydrous tin(IV) oxide (SnO₂). Any remaining organic matter or nitrogen-containing residues are burned off. The high temperature ensures complete oxidation of Sn²⁺ to Sn⁴⁺.',
            observe: [
              'The crucible glows red-orange when sufficiently hot — maintain this for at least 10 minutes',
              'The lid must be kept slightly ajar to allow gases to escape during heating',
              'A white or cream-colored residue indicates successful formation of SnO₂',
              'A blue-black or red residue indicates incomplete oxidation (SnO) — continue heating',
            ],
          }
        },
        {
          id: 3,
          title: 'Observe Residue Color',
          shortDescription: 'Note the color of the residue (indicates SnO or SnO₂)',
          handler: 'handleObserveResidue',
          infoContent: {
            title: 'Product Identification',
            content: 'Tin(IV) oxide (SnO₂) occurs as a white powdery solid. Tin(II) oxide (SnO) can occur as a stable blue/black solid or a metastable red form. Examine your sample — does its appearance match the results of your calculations?',
            whatHappens: 'You examine the color of the residue in the crucible. The color is a direct indicator of which tin oxide was formed, providing a qualitative check on your quantitative calculations.',
            observe: [
              'White or cream powder indicates SnO₂ (tin IV oxide) — the expected product',
              'Blue-black solid indicates stable SnO (tin II oxide) — incomplete oxidation occurred',
              'Red solid indicates the metastable form of SnO — also incomplete oxidation',
              'Compare the observed color with what your mole ratio calculation predicts',
            ],
          }
        }
      ]
    },
    {
      id: 7,
      name: 'final_weighing',
      title: 'Part VII: Cool and Weigh to Constant Mass',
      equipment: ['Crucible tongs', 'Desiccator', 'Analytical balance'],
      initialState: {
        coolingProgress: 0,
        finalMass: null,
        previousMass: null,
        constantMassAchieved: false,
        massAttempts: []
      },
      steps: [
        {
          id: 1,
          title: 'Transfer to Desiccator',
          shortDescription: 'Use tongs to transfer hot crucible to desiccator',
          handler: 'handleTransferToDesiccator'
        },
        {
          id: 2,
          title: 'Cool Completely',
          shortDescription: 'Allow to cool to room temperature in the desiccator',
          handler: 'handleCoolCompletely',
          infoContent: {
            title: 'Complete Cooling',
            content: 'Allow the crucible to cool to room temperature in a desiccator. Then weigh it as accurately as you can. Hot objects cause convection currents that affect balance readings.',
            whatHappens: 'The hot crucible containing the tin oxide product cools to room temperature inside the sealed desiccator. The dry atmosphere prevents moisture absorption. Only when fully cooled can an accurate mass be obtained.',
            observe: [
              'Hot objects create convection currents near the balance pan, causing unstable and inaccurate readings',
              'The crucible should feel at room temperature when touched briefly with the back of your hand near (not on) it',
              'Allow at least 10 minutes of cooling time in the desiccator before attempting to weigh',
            ],
          }
        },
        {
          id: 3,
          title: 'Weigh Final Product',
          shortDescription: 'Weigh crucible with lid and product',
          handler: 'handleWeighFinal'
        },
        {
          id: 4,
          title: 'Check Constant Mass',
          shortDescription: 'Repeat heating and cooling until constant weight within 0.01 g',
          handler: 'handleCheckConstantMass',
          validation: {
            requires: ['constantMassAchieved']
          },
          infoContent: {
            title: 'Constant Mass',
            content: 'Repeat heating and cooling until the crucible reaches constant weight. If the mass differs by more than 0.01 g from the previous weighing, heat strongly again, cool to room temperature in a desiccator, and reweigh. Repeat until two consecutive masses agree within **0.01 g**. Record all masses to **0.001 g**.',
            whatHappens: 'You cycle through strong heating, cooling in the desiccator, and weighing until two consecutive masses agree within 0.01 g. This ensures the oxidation reaction is truly complete and all volatile matter has been driven off.',
            observe: [
              'Compare each mass reading with the previous one — agreement within 0.01 g means constant mass is achieved',
              'If mass is still decreasing, the reaction may not be complete — heat strongly again',
              'Record every mass to 0.001 g, even intermediate readings that do not meet the criterion',
            ],
          }
        }
      ]
    },
    {
      id: 8,
      name: 'calculations',
      title: 'Part VIII: Calculate Empirical Formula',
      equipment: ['Calculator', 'Data table'],
      initialState: {
        massOfOxygen: null,
        molesOfTin: null,
        molesOfOxygen: null,
        moleRatio: null,
        empiricalFormula: null
      },
      steps: [
        {
          id: 1,
          title: 'Calculate Mass of Oxygen',
          shortDescription: 'Find mass of oxygen by difference',
          handler: 'handleCalculateOxygenMass',
          infoContent: {
            title: 'Mass by Difference',
            content: 'Mass of O = weight of (crucible + lid + contents after heating) - weight of (crucible + lid) - mass of Sn.',
            whatHappens: 'You calculate the mass of oxygen that combined with the tin by subtracting the known masses. The final crucible mass minus the empty crucible mass gives the mass of tin oxide product; subtracting the original tin mass gives the mass of oxygen incorporated.',
            observe: [
              'Mass of tin oxide product = (final crucible + lid + contents) minus (empty crucible + lid at constant mass)',
              'Mass of oxygen = mass of tin oxide product minus original mass of tin',
              'All mass values should be in grams to 0.001 g for consistency',
            ],
          }
        },
        {
          id: 2,
          title: 'Calculate Moles of Tin',
          shortDescription: 'Convert mass of tin to moles',
          handler: 'handleCalculateMolesTin',
          infoContent: {
            title: 'Moles of Tin',
            content: 'Moles Sn = Mass Sn / 118.71 g/mol',
            whatHappens: 'You convert the measured mass of tin to moles using the molar mass of tin (118.71 g/mol). This is one of the two mole values needed to determine the empirical formula ratio.',
            observe: [
              'For ~1.0 g of tin, expect approximately 0.0084 mol Sn',
              'Use the exact mass you weighed, not the approximate 1.0 g target',
              'Keep at least 4 significant figures in the moles value to avoid rounding errors in the ratio',
            ],
          }
        },
        {
          id: 3,
          title: 'Calculate Moles of Oxygen',
          shortDescription: 'Convert mass of oxygen to moles',
          handler: 'handleCalculateMolesOxygen',
          infoContent: {
            title: 'Moles of Oxygen',
            content: 'Moles O = Mass O / 16.00 g/mol',
            whatHappens: 'You convert the calculated mass of oxygen to moles using the molar mass of atomic oxygen (16.00 g/mol). Note: use the atomic mass of O, not the molecular mass of O₂, because the formula refers to individual oxygen atoms.',
            observe: [
              'For SnO₂ formation from ~1.0 g Sn, expect approximately 0.017 mol O (roughly twice the moles of Sn)',
              'Use 16.00 g/mol (atomic oxygen), not 32.00 g/mol (molecular oxygen O₂)',
              'Keep at least 4 significant figures to maintain accuracy in the subsequent ratio calculation',
            ],
          }
        },
        {
          id: 4,
          title: 'Find Mole Ratio',
          shortDescription: 'Divide by the smaller number of moles',
          handler: 'handleFindRatio',
          infoContent: {
            title: 'Finding the Ratio',
            content: 'The ratio is obtained by dividing the larger number of moles by the smaller number. Round to the nearest whole number. Example: if you had 0.199 moles of oxygen and 0.400 moles of hydrogen, then 0.400/0.199 = 2.01, which rounds to 2, giving a 2:1 ratio (H₂O).',
            whatHappens: 'You divide both mole values by the smaller one to obtain the simplest whole number ratio of Sn to O. This ratio directly gives the subscripts in the empirical formula.',
            observe: [
              'Divide moles of O by moles of Sn (or vice versa) — the smaller value becomes 1',
              'The result should be close to a whole number (1.0 for SnO, or 2.0 for SnO₂)',
              'Values within about 0.1 of a whole number can be rounded; values like 1.5 would indicate a different ratio',
            ],
          }
        },
        {
          id: 5,
          title: 'Determine Empirical Formula',
          shortDescription: 'Write the empirical formula based on mole ratio',
          handler: 'handleDetermineFormula',
          infoContent: {
            title: 'Empirical Formula',
            content: 'If the ratio is 1:1, the formula is SnO. If the ratio is 1:2, the formula is SnO₂. Most students obtain SnO₂ (tin IV oxide).',
            whatHappens: 'You write the empirical formula using the mole ratio as subscripts. The Sn:O ratio of 1:2 gives SnO₂ (tin IV oxide), while a 1:1 ratio would give SnO (tin II oxide). You then compare the predicted product with the observed residue color.',
            observe: [
              'A ratio near 1:2 (Sn:O) confirms SnO₂ — this is the most common result when heating is thorough',
              'A ratio near 1:1 (Sn:O) suggests SnO — usually due to incomplete oxidation',
              'Compare your formula with the residue color: white = SnO₂, blue-black or red = SnO',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp11',
    title: 'Empirical Formula Quiz',
    questions: [
      {
        id: 'exp11-q1',
        type: 'mcq' as const,
        question: 'What is the difference between an empirical formula and a molecular formula?',
        options: [
          { id: 'a', text: 'They are always the same thing' },
          { id: 'b', text: 'The empirical formula shows the simplest whole-number ratio of atoms; the molecular formula shows the actual number of atoms' },
          { id: 'c', text: 'The molecular formula shows the ratio; the empirical formula shows the actual number' },
          { id: 'd', text: 'The empirical formula only applies to ionic compounds' },
        ],
        correctOptionId: 'b',
        explanation: 'The empirical formula gives the simplest whole-number ratio of atoms in a compound (e.g., CH₂O), while the molecular formula gives the actual number of atoms per molecule (e.g., C₆H₁₂O₆ for glucose). For ionic compounds like tin oxide, the empirical formula is typically the same as the formula unit.',
      },
      {
        id: 'exp11-q2',
        type: 'mcq' as const,
        question: 'Why must the crucible be heated to constant mass before adding the tin sample?',
        options: [
          { id: 'a', text: 'To sterilize the crucible' },
          { id: 'b', text: 'To remove any moisture or volatile impurities that would affect mass measurements' },
          { id: 'c', text: 'To make the crucible easier to handle' },
          { id: 'd', text: 'To change the color of the crucible' },
        ],
        correctOptionId: 'b',
        explanation: 'Heating to constant mass removes moisture, adsorbed gases, and volatile impurities from the crucible. If these are not removed, they would contribute to the initial mass and then be lost during later heating, causing errors in the mass of tin oxide formed.',
      },
      {
        id: 'exp11-q3',
        type: 'mcq' as const,
        question: 'In this experiment, concentrated HNO₃ is used to react with tin. What is the role of the nitric acid?',
        options: [
          { id: 'a', text: 'It acts as a reducing agent' },
          { id: 'b', text: 'It dissolves the tin by oxidizing it, forming tin nitrate and releasing NO₂ gas' },
          { id: 'c', text: 'It acts as a catalyst only' },
          { id: 'd', text: 'It removes impurities from the crucible' },
        ],
        correctOptionId: 'b',
        explanation: 'Concentrated HNO₃ is a strong oxidizing acid that oxidizes tin metal (Sn⁰) to tin ions (Sn\u00B2⁺ or Sn⁴⁺). The nitric acid is reduced to toxic brown NO₂ gas. Upon subsequent heating, the tin nitrate decomposes to form tin oxide.',
      },
      {
        id: 'exp11-q4',
        type: 'short_answer' as const,
        question: 'Explain how the mass of oxygen in the tin oxide product is determined experimentally, given that you only measure the mass of tin and the mass of the final tin oxide.',
        sampleAnswer: 'The mass of oxygen is found by difference: mass of oxygen = mass of tin oxide product - mass of original tin. Since the only elements in the product are tin and oxygen, subtracting the known mass of tin from the total mass of the oxide gives the mass of oxygen that combined with the tin. This relies on the law of conservation of mass.',
      },
      {
        id: 'exp11-q5',
        type: 'calculation' as const,
        question: 'A student starts with 1.000 g of tin (Sn, atomic mass = 118.71 g/mol) and obtains 1.270 g of tin oxide. Calculate the mole ratio of O to Sn and determine the empirical formula. Enter the number of oxygen atoms per tin atom.',
        expectedValue: 2,
        tolerance: 5,
        unit: 'O atoms per Sn',
      },
    ],
  },
};

// Temperature-based crucible colors
export const TEMPERATURE_COLORS = {
  roomTemp: { temp: 25, color: '#D3D3D3', description: 'Gray ceramic' },
  warm: { temp: 200, color: '#C8B8A8', description: 'Warm gray' },
  hot: { temp: 500, color: '#FFB366', description: 'Orange tint' },
  veryHot: { temp: 700, color: '#FF6B35', description: 'Red-orange' },
  glowing: { temp: 900, color: '#FF4444', description: 'Bright red glow' }
};

// Tin and oxide colors
export const MATERIAL_COLORS = {
  tinFresh: { color: '#C0C0C0', description: 'Shiny silver' },
  tinReacting: { color: '#A0A0A0', description: 'Dull gray' },
  snO: { color: '#1C1C3C', description: 'Blue-black (tin II oxide)' },
  snOMetastable: { color: '#8B0000', description: 'Red (metastable tin II oxide)' },
  snO2: { color: '#FFFEF5', description: 'White powder (tin IV oxide)' }
};

// NO2 fumes
export const FUMES_DATA = {
  color: '#8B4513',
  description: 'Brown NO2 gas',
  toxicity: 'TOXIC - work in fume hood only'
};

// Desiccator components
export const DESICCATOR_COLORS = {
  glassBody: { color: '#E6F3FF', description: 'Clear blue tint' },
  silicaGelDry: { color: '#4169E1', description: 'Blue (dry)' },
  silicaGelMoist: { color: '#FFB6C1', description: 'Pink (needs replacing)' },
  base: { color: '#F5F5F5', description: 'White ceramic' }
};

// Expected results
export const EXPECTED_RESULTS = {
  snO2: {
    formula: 'SnO₂',
    moleRatioSn: 1,
    moleRatioO: 2,
    color: 'white/cream',
    description: 'Tin(IV) oxide - most common product'
  },
  snO: {
    formula: 'SnO',
    moleRatioSn: 1,
    moleRatioO: 1,
    color: 'blue-black or red',
    description: 'Tin(II) oxide - less common'
  }
};

// Atomic masses
export const ATOMIC_MASSES = {
  Sn: 118.71,
  O: 16.00
};

export default experiment11Config;
